class ProductPurchase extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.select = this.querySelector('[data-variant-select]');
    this.quantity = this.querySelector('[data-quantity]');
    this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.dataset.currency || 'USD' });
    this.select?.addEventListener('change', () => this.updateVariant(true));
    this.quantity?.addEventListener('input', () => this.updateQuantityChoices());
    this.querySelectorAll('[data-set-quantity]').forEach(button => {
      button.addEventListener('click', () => this.setQuantity(Number(button.dataset.setQuantity)));
    });
    this.querySelectorAll('[data-quantity-direction]').forEach(button => {
      button.addEventListener('click', () => {
        const step = Number(this.quantity.step) || 1;
        this.setQuantity(Number(this.quantity.value) + Number(button.dataset.quantityDirection) * step);
      });
    });
    this.updateVariant(false);
  }

  allowedQuantity(value) {
    const min = Number(this.quantity.min) || 1;
    const step = Number(this.quantity.step) || 1;
    const max = this.quantity.max ? Number(this.quantity.max) : Infinity;
    return Number.isInteger(value) && value >= min && value <= max && (value - min) % step === 0;
  }

  setQuantity(value) {
    if (!this.allowedQuantity(value)) return;
    this.quantity.value = String(value);
    this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
    this.updateQuantityChoices();
  }

  discountCents(quantity) {
    const eligible = this.dataset.bundleEnabled === 'true' && this.select?.value === this.dataset.bundleVariant && this.dataset.currency === 'USD';
    if (!eligible) return 0;
    return quantity >= 3 ? Number(this.dataset.bundleThree || 0) : quantity >= 2 ? Number(this.dataset.bundleTwo || 0) : 0;
  }
  totalCents(quantity) { return Math.max(0, Number(this.select?.selectedOptions[0]?.dataset.cents || 0) * quantity - this.discountCents(quantity)); }

  updateQuantityChoices() {
    const quantity = Number(this.quantity.value);
    const step = Number(this.quantity.step) || 1;
    this.querySelectorAll('[data-set-quantity]').forEach(button => {
      const value = Number(button.dataset.setQuantity);
      button.disabled = !this.allowedQuantity(value);
      button.setAttribute('aria-pressed', String(value === quantity));
      const card = button.closest('[data-quantity-offer]');
      if (card) { card.toggleAttribute('data-selected', value === quantity); const variants = card.querySelector('.offer-variants'); if (variants) variants.hidden = value !== quantity; }
    });
    this.querySelectorAll('[data-quantity-direction]').forEach(button => {
      button.disabled = !this.allowedQuantity(quantity + Number(button.dataset.quantityDirection) * step);
    });
    const option = this.select?.selectedOptions[0];
    const section = this.closest('.product-section');
    const price = section?.querySelector('[data-sticky-price]');
    const caption = section?.querySelector('[data-sticky-quantity]');
    const sticky = section?.querySelector('[data-sticky-add]');
    const valid = this.allowedQuantity(quantity);
    if (price && option) price.textContent = valid ? this.money.format(this.totalCents(quantity) / 100) : '—';
    if (caption) caption.textContent = (this.dataset.selectedTemplate || '__COUNT__').replace('__COUNT__', valid ? String(quantity) : '—');
    if (sticky && option) sticky.disabled = option.dataset.available !== 'true' || !valid;
    requestAnimationFrame(syncStickyPurchase);
  }

  updateVariant(updateHistory) {
    const option = this.select?.selectedOptions[0];
    if (!option || !this.quantity) return;
    const data = option.dataset;
    const available = data.available === 'true';
    this.querySelector('span[data-price]').textContent = data.price;
    const compare = this.querySelector('[data-compare]');
    compare.textContent = data.compare;
    compare.hidden = data.onSale !== 'true';
    const add = this.querySelector('[data-add]');
    add.disabled = !available;
    this.querySelector('[data-add-text]').textContent = available ? this.dataset.addLabel : this.dataset.soldLabel;
    const title = data.title === 'Default Title' ? 'White' : data.title || option.textContent.split('—')[0].trim();
    this.closest('.product-section')?.querySelectorAll('[data-offer-finish] option, [data-sticky-finish] option').forEach(item => { item.value = option.value; item.textContent = title; });
    const sticky = this.closest('.product-section')?.querySelector('[data-sticky-add]');
    if (sticky) sticky.textContent = available ? this.dataset.addLabel : this.dataset.soldLabel;
    const dynamic = this.querySelector('[data-dynamic-checkout]');
    if (dynamic) dynamic.hidden = !available;
    this.quantity.min = data.min || '1';
    this.quantity.step = data.step || '1';
    if (data.max) this.quantity.max = data.max;
    else this.quantity.removeAttribute('max');
    if (!this.allowedQuantity(Number(this.quantity.value))) this.quantity.value = this.quantity.min;
    this.querySelectorAll('[data-offer-total]').forEach(total => {
      const count = Number(total.dataset.offerTotal);
      total.textContent = this.money.format(this.totalCents(count) / 100);
      const saving = this.querySelector(`[data-offer-save="${count}"]`);
      const regular = this.querySelector(`[data-offer-regular="${count}"]`);
      if (saving) { saving.hidden = this.discountCents(count) === 0; saving.textContent = `${this.dataset.saveLabel || 'Save'} ${this.money.format(this.discountCents(count) / 100)}`; }
      if (regular) { regular.hidden = this.discountCents(count) === 0; regular.textContent = this.money.format(Number(data.cents) * count / 100); regular.title = this.dataset.separatelyLabel || 'bought separately'; }
    });
    this.querySelectorAll('[data-value-badge], [data-bundle-note]').forEach(node => { node.hidden = this.discountCents(3) === 0; });
    this.updateQuantityChoices();
    if (updateHistory && this.dataset.updateUrl === 'true') {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', option.value);
      history.replaceState({}, '', url);
    }
    if (data.media) activateMedia(this.closest('.product-section')?.querySelector('[data-gallery]'), data.media);
  }
}

function activateMedia(gallery, id, { scroll = true, behavior } = {}) {
  const selected = gallery?.querySelector(`[data-media-id="${CSS.escape(id)}"]`);
  if (!selected) return;
  selected.querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
  gallery.querySelectorAll('[data-media-id]').forEach(slide => {
    slide.hidden = slide !== selected;
    if (slide !== selected) {
      slide.querySelectorAll('video').forEach(video => video.pause());
      slide.querySelectorAll('iframe').forEach(frame => {
        const message = frame.src.includes('youtube') ? JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }) : JSON.stringify({ method: 'pause' });
        frame.contentWindow?.postMessage(message, new URL(frame.src, location.href).origin);
      });
    }
  });
  const slides = [...gallery.querySelectorAll('[data-media-id]')];
  const counter = gallery.querySelector('[data-gallery-counter]');
  if (counter) counter.textContent = `${slides.indexOf(selected) + 1} / ${slides.length}`;
  gallery.querySelectorAll('[data-gallery-target]').forEach(link => link.setAttribute('aria-current', String(link.dataset.galleryTarget === id)));
  const zoom = gallery.querySelector('[data-gallery-zoom]');
  if (zoom) zoom.hidden = !selected.querySelector('img');
  if (scroll && gallery.closest('.paw-product') && matchMedia('(max-width:749px)').matches) {
    const viewport = gallery.querySelector('.product-gallery__main');
    const left = Math.min(selected.offsetLeft, viewport.scrollWidth - viewport.clientWidth);
    if (Math.abs(viewport.scrollLeft - left) > 2) viewport.scrollTo({ left, behavior: behavior || (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth') });
  }
}

const lightboxStates = new WeakMap();

function setLightboxZoom(dialog, scale, focalPoint) {
  const viewport = dialog.querySelector('[data-lightbox-viewport]');
  if (!viewport) return;
  const state = lightboxStates.get(dialog) || { scale: 1 };
  const frame = dialog.querySelector('[data-lightbox-content]').firstElementChild;
  if (!frame) return;
  const old = frame.getBoundingClientRect();
  const view = viewport.getBoundingClientRect();
  const point = focalPoint || {
    x: old.width ? (view.left + viewport.clientWidth / 2 - old.left) / old.width : .5,
    y: old.height ? (view.top + viewport.clientHeight / 2 - old.top) / old.height : .5
  };
  state.scale = Math.min(3, Math.max(1, scale));
  lightboxStates.set(dialog, state);
  const size = Math.min(viewport.clientWidth, viewport.clientHeight) * state.scale;
  dialog.style.setProperty('--lightbox-size', `${size}px`);
  dialog.querySelector('[data-lightbox-level]').textContent = `${state.scale}×`;
  dialog.querySelector('[data-lightbox-step="-1"]').disabled = state.scale <= 1;
  dialog.querySelector('[data-lightbox-step="1"]').disabled = state.scale >= 3;
  requestAnimationFrame(() => {
    if (!dialog.open) return;
    const rect = frame.getBoundingClientRect();
    const box = viewport.getBoundingClientRect();
    viewport.scrollLeft += rect.left - box.left + rect.width * point.x - viewport.clientWidth / 2;
    viewport.scrollTop += rect.top - box.top + rect.height * point.y - viewport.clientHeight / 2;
  });
}

function changeLightboxZoom(dialog, step) {
  setLightboxZoom(dialog, (lightboxStates.get(dialog)?.scale || 1) + step * .5);
}

if (!customElements.get('product-purchase')) customElements.define('product-purchase', ProductPurchase);

document.addEventListener('click', event => {
  const thumbnail = event.target.closest('[data-gallery-target]');
  if (thumbnail) {
    event.preventDefault();
    activateMedia(thumbnail.closest('[data-gallery]'), thumbnail.dataset.galleryTarget);
  }
  const step = event.target.closest('[data-gallery-step]');
  if (step) {
    const gallery = step.closest('[data-gallery]');
    const slides = [...gallery.querySelectorAll('[data-media-id]')];
    if (slides.length) {
      const current = Math.max(0, slides.findIndex(slide => !slide.hidden));
      activateMedia(gallery, slides[(current + Number(step.dataset.galleryStep) + slides.length) % slides.length].dataset.mediaId);
    }
  }
  const zoom = event.target.closest('[data-gallery-zoom]');
  if (zoom) {
    const gallery = zoom.closest('[data-gallery]');
    const source = gallery.querySelector('.gallery-slide:not([hidden]) img, .product-gallery__main>img');
    const dialog = gallery.querySelector('[data-gallery-lightbox]');
    if (source && dialog?.showModal) {
      const image = new Image();
      image.src = source.currentSrc || source.src;
      image.alt = source.alt;
      image.draggable = false;
      image.loading = 'eager';
      const frame = source.closest('.supplier-frame');
      if (frame) { const copy = document.createElement('div'); copy.className = frame.className; copy.append(image); dialog.querySelector('[data-lightbox-content]').replaceChildren(copy); } else dialog.querySelector('[data-lightbox-content]').replaceChildren(image);
      lightboxStates.set(dialog, { scale: 1, trigger: zoom });
      dialog.showModal();
      setLightboxZoom(dialog, 2, { x: .5, y: .5 });
      dialog.querySelector('[data-lightbox-viewport]')?.focus({ preventScroll: true });
      syncStickyPurchase();
    }
  }
  const zoomStep = event.target.closest('[data-lightbox-step]');
  if (zoomStep) changeLightboxZoom(zoomStep.closest('dialog'), Number(zoomStep.dataset.lightboxStep));
  const fit = event.target.closest('[data-lightbox-fit]');
  if (fit) setLightboxZoom(fit.closest('dialog'), 1, { x: .5, y: .5 });
  if (event.target.matches('dialog[data-gallery-lightbox]')) event.target.close();
  const sticky = event.target.closest('[data-sticky-add]');
  if (sticky) {
    const purchase = sticky.closest('.product-section').querySelector('product-purchase');
    const add = purchase?.querySelector('[data-add]');
    if (add && !add.disabled) purchase.querySelector('form').requestSubmit(add);
  }
  const menuLink = event.target.closest('.mobile-menu__panel a');
  if (menuLink) menuLink.closest('details')?.removeAttribute('open');
});

document.addEventListener('keydown', event => {
  const dialog = document.querySelector('dialog[data-gallery-lightbox][open]');
  if (dialog && ['+', '=', '-', '0'].includes(event.key)) {
    event.preventDefault();
    if (event.key === '0') setLightboxZoom(dialog, 1, { x: .5, y: .5 });
    else changeLightboxZoom(dialog, event.key === '-' ? -1 : 1);
    return;
  }
  if (document.querySelector('dialog[open]')) return;
  if (event.key === 'Escape') document.querySelectorAll('.mobile-menu[open]').forEach(menu => {
    menu.open = false;
    menu.querySelector('summary')?.focus();
  });
});

document.addEventListener('close', event => {
  if (event.target.matches('dialog[data-gallery-lightbox]')) {
    event.target.querySelector('[data-lightbox-content]').replaceChildren();
    lightboxStates.get(event.target)?.trigger?.focus({ preventScroll: true });
    lightboxStates.delete(event.target);
  }
}, true);

function syncStickyPurchase() {
  document.querySelectorAll('[data-sticky-purchase]').forEach(bar => {
    const section = bar.closest('.product-section');
    const add = section?.querySelector('[data-add]');
    const info = section?.querySelector('.product-info');
    if (!add || !info) return;
    const rect = add.getBoundingClientRect();
    const overlayOpen = document.querySelector('dialog[open], .mobile-menu[open]');
    const needed = rect.bottom <= 0;
    bar.hidden = !needed || Boolean(overlayOpen);
  });
}
let stickyFramePending = false;
window.addEventListener('scroll', () => {
  if (!stickyFramePending) { stickyFramePending = true; requestAnimationFrame(() => { stickyFramePending = false; syncStickyPurchase(); }); }
}, { passive: true });
window.addEventListener('resize', syncStickyPurchase);
document.addEventListener('toggle', syncStickyPurchase, true);
document.addEventListener('close', syncStickyPurchase, true);
if ('IntersectionObserver' in window) document.querySelectorAll('[data-sticky-purchase]').forEach(bar => { const add = bar.closest('.product-section')?.querySelector('[data-add]'); if (add) new IntersectionObserver(syncStickyPurchase).observe(add); });
syncStickyPurchase();

document.querySelectorAll('[data-gallery]').forEach(gallery => { const zoom = gallery.querySelector('[data-gallery-zoom]'); if (zoom) zoom.hidden = !gallery.querySelector('.gallery-slide:not([hidden]) img, .product-gallery__main>img'); });

document.addEventListener('click', event => { const button = event.target.closest('[data-demo-step]'); if (!button) return; const rail = button.closest('.demo-carousel').querySelector('.demo-rail'); rail.querySelectorAll('video').forEach(video => video.pause()); if (Number(button.dataset.demoStep) > 0) rail.append(rail.firstElementChild); else rail.prepend(rail.lastElementChild); rail.scrollLeft = 0; });

// Mouse dragging complements native touch/trackpad scrolling and keyboard arrows.
let lightboxDrag;
document.addEventListener('pointerdown', event => {
  const viewport = event.target.closest('[data-lightbox-viewport]');
  if (!viewport || event.pointerType !== 'mouse' || event.button !== 0) return;
  lightboxDrag = { viewport, id: event.pointerId, x: event.clientX, y: event.clientY, left: viewport.scrollLeft, top: viewport.scrollTop };
  viewport.setPointerCapture(event.pointerId);
  viewport.setAttribute('data-dragging', '');
  event.preventDefault();
});
document.addEventListener('pointermove', event => {
  if (!lightboxDrag || event.pointerId !== lightboxDrag.id) return;
  const drag = lightboxDrag;
  drag.viewport.scrollLeft = drag.left + drag.x - event.clientX;
  drag.viewport.scrollTop = drag.top + drag.y - event.clientY;
});
function endLightboxDrag(event) {
  if (!lightboxDrag || event.pointerId !== lightboxDrag.id) return;
  lightboxDrag.viewport.removeAttribute('data-dragging');
  lightboxDrag = undefined;
}
document.addEventListener('pointerup', endLightboxDrag);
document.addEventListener('pointercancel', endLightboxDrag);
window.addEventListener('resize', () => {
  document.querySelectorAll('[data-gallery-lightbox][open]').forEach(dialog => setLightboxZoom(dialog, lightboxStates.get(dialog)?.scale || 2));
});
